/**
 * UtilityAdminSettings — the "Configuration" hub for the UtilityAdmin role:
 * Sections, Subjects, Assessments, Question Types, Tuition Library, and
 * Sessions & Devices. (Imports, Holidays and Events are now separate sidebar
 * pages; there is no "Calls" tab.)
 */
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { apiErr } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LayoutGrid, BookOpen, Library, Settings as SettingsIcon, ClipboardList, MonitorSmartphone, ListChecks, FileText, Building2, Megaphone, CreditCard, ScanFace } from "lucide-react";
import AdminSections from "@/pages/AdminSections";
import AdminSubjects from "@/pages/AdminSubjects";
import AdminTuitionLibrary from "@/pages/AdminTuitionLibrary";
import AdminAssessmentSettings from "@/pages/AdminAssessmentSettings";
import AdminSessions from "@/pages/AdminSessions";
import UtilityAdminQuestionTypes from "@/pages/UtilityAdminQuestionTypes";
import TeacherUtility from "@/pages/TeacherUtility";
import SchoolProfile from "@/pages/SchoolProfile";
import Announcements from "@/pages/Announcements";
import WccBalanceCard from "@/components/WccBalanceCard";

const VALID_TABS = ["sections", "subjects", "assessments", "reports", "announcements", "school-profile", "question-types", "tuition-library", "sessions", "wcc", "ai-attendance"];

function AiAttendanceToggle() {
  const [rules, setRules] = useState(null);
  useEffect(() => {
    api.get("/admin/face-attendance/rules").then(({ data }) => setRules(data)).catch(() => toast.error("Failed to load AI attendance settings"));
  }, []);
  const toggle = async (field, v) => {
    try {
      const { data } = await api.put("/admin/face-attendance/rules", { ...rules, [field]: v });
      setRules(data);
      toast.success(v ? "Turned ON" : "Turned OFF");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
  };
  if (!rules) return <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>;
  return (
    <div className="max-w-xl space-y-4" data-testid="utility-ai-attendance">
      <div className={`rounded-2xl border p-5 flex items-center gap-4 ${rules.ai_enabled ? "border-emerald-200 bg-emerald-50/40 dark:border-emerald-900/40 dark:bg-emerald-950/20" : "border-rose-200 bg-rose-50/40 dark:border-rose-900/40 dark:bg-rose-950/20"}`}>
        <Switch checked={rules.ai_enabled} onCheckedChange={(v) => toggle("ai_enabled", v)} data-testid="utility-ai-enabled-switch" />
        <div>
          <div className="font-semibold text-slate-800 dark:text-slate-100">AI Face Attendance {rules.ai_enabled ? "ON" : "OFF"}</div>
          <div className="text-xs text-slate-500">When OFF, all camera check-ins are ignored and no attendance is auto-marked.</div>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 flex items-center gap-4">
        <Switch checked={!!rules.absent_alerts_enabled} onCheckedChange={(v) => toggle("absent_alerts_enabled", v)} data-testid="utility-absent-alerts-switch" />
        <div>
          <div className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Absent Alerts to Parents (WhatsApp)</div>
          <div className="text-xs text-slate-500">Automatic message at attendance close ({rules.attendance_close}) to parents of undetected students.</div>
        </div>
      </div>
      <p className="text-xs text-slate-400">Full camera & rules management is in the Admin portal → AI Attendance.</p>
    </div>
  );
}

export default function UtilityAdminSettings() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const active = VALID_TABS.includes(requested) ? requested : "sections";
  const onChange = (val) => setParams({ tab: val }, { replace: true });

  return (
    <Layout>
      <div className="space-y-6" data-testid="utility-settings-page">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300 flex items-center justify-center">
            <SettingsIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Configuration</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Manage sections, subjects, assessments, question types, library and sessions.</p>
          </div>
        </div>

        <Tabs value={active} onValueChange={onChange} className="w-full">
          <TabsList className="h-auto flex-wrap gap-1 bg-slate-100 dark:bg-slate-800/60" data-testid="utility-settings-tabs">
            <TabsTrigger value="sections" data-testid="utility-tab-sections" className="gap-1.5">
              <LayoutGrid className="h-4 w-4" /> Sections
            </TabsTrigger>
            <TabsTrigger value="subjects" data-testid="utility-tab-subjects" className="gap-1.5">
              <BookOpen className="h-4 w-4" /> Subjects
            </TabsTrigger>
            <TabsTrigger value="assessments" data-testid="utility-tab-assessments" className="gap-1.5">
              <ClipboardList className="h-4 w-4" /> Assessments
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="utility-tab-reports" className="gap-1.5">
              <FileText className="h-4 w-4" /> Reports
            </TabsTrigger>
            <TabsTrigger value="announcements" data-testid="utility-tab-announcements" className="gap-1.5">
              <Megaphone className="h-4 w-4" /> Announcements
            </TabsTrigger>
            <TabsTrigger value="school-profile" data-testid="utility-tab-school-profile" className="gap-1.5">
              <Building2 className="h-4 w-4" /> School Profile
            </TabsTrigger>
            <TabsTrigger value="question-types" data-testid="utility-tab-question-types" className="gap-1.5">
              <ListChecks className="h-4 w-4" /> Question Types
            </TabsTrigger>
            <TabsTrigger value="tuition-library" data-testid="utility-tab-tuition-library" className="gap-1.5">
              <Library className="h-4 w-4" /> Tuition Library
            </TabsTrigger>
            <TabsTrigger value="sessions" data-testid="utility-tab-sessions" className="gap-1.5">
              <MonitorSmartphone className="h-4 w-4" /> Sessions &amp; Devices
            </TabsTrigger>
            <TabsTrigger value="wcc" data-testid="utility-tab-wcc" className="gap-1.5">
              <CreditCard className="h-4 w-4" /> WCC Credits
            </TabsTrigger>
            <TabsTrigger value="ai-attendance" data-testid="utility-tab-ai-attendance" className="gap-1.5">
              <ScanFace className="h-4 w-4" /> AI Attendance
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sections" className="mt-6"><AdminSections embedded /></TabsContent>
          <TabsContent value="subjects" className="mt-6"><AdminSubjects embedded /></TabsContent>
          <TabsContent value="assessments" className="mt-6"><AdminAssessmentSettings /></TabsContent>
          <TabsContent value="reports" className="mt-6"><TeacherUtility embedded /></TabsContent>
          <TabsContent value="announcements" className="mt-6"><Announcements embedded /></TabsContent>
          <TabsContent value="school-profile" className="mt-6"><SchoolProfile /></TabsContent>
          <TabsContent value="question-types" className="mt-6"><UtilityAdminQuestionTypes embedded /></TabsContent>
          <TabsContent value="tuition-library" className="mt-6"><AdminTuitionLibrary embedded /></TabsContent>
          <TabsContent value="sessions" className="mt-6"><AdminSessions /></TabsContent>
          <TabsContent value="wcc" className="mt-6"><WccBalanceCard /></TabsContent>
          <TabsContent value="ai-attendance" className="mt-6"><AiAttendanceToggle /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
